import {
	cn,
	deliveryMethodIcons,
	LogoPay,
	TopPharmacyBadge,
} from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { cva, type VariantProps } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { Price } from "~/components/price.component";
import { PriceComparisonDiscountBadge } from "~/features/price-comparison/price-comparison-discount-badge.component";
import { PriceComparisonOldPrice } from "~/features/price-comparison/price-comparison-old-price.component";
import { isCombinedPaymentEnabled } from "~/features/products/combined-payment.utils";
import { HighlightMatch } from "~/features/products/pharmacy-picker/highlight-match.component";
import type { PharmacyPickerItem } from "~/features/products/pharmacy-picker/pharmacy-picker.types";
import { formatPickerDistanceLabel } from "~/features/products/pharmacy-picker/pharmacy-picker.utils";
import type { OfferCardDeliveryKind } from "~/features/products/product-details-vendor-select-trigger.component";

const pharmacyPickerCardVariant = cva(
	"justify-center whitespace-nowrap text-body-emphasis ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ansay-main focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-gray-200 disabled:border-gray-200 disabled:text-gray-500 w-full min-w-0 flex flex-col items-start gap-1 bg-white p-3 rounded-xl hover:bg-gray-100 cursor-pointer text-left",
	{
		variants: {
			variant: {
				selected: "border-2 border-ansay-main outline-ansay-main",
				normal: "border-2 border-gray-200 outline-ansay-main",
			},
		},
		defaultVariants: {
			variant: "normal",
		},
	},
);

const getDeliveryIcon = (kind: OfferCardDeliveryKind) =>
	deliveryMethodIcons[kind];

export type PharmacyPickerCardBadgeLayout = "listing" | "pdp";

export function PharmacyPickerCard({
	item,
	isSelected,
	onSelect,
	showPrice = false,
	showDistance = false,
	product,
	searchQuery,
	badgeLayout = "listing",
}: {
	item: PharmacyPickerItem;
	isSelected: boolean;
	onSelect: () => void;
	showPrice?: boolean;
	showDistance?: boolean;
	product?: ProductNoEdibleInfo;
	searchQuery?: string;
	badgeLayout?: PharmacyPickerCardBadgeLayout;
} & VariantProps<typeof pharmacyPickerCardVariant>) {
	const { i18n } = useTranslation();
	const distanceLabel =
		showDistance &&
		formatPickerDistanceLabel({
			distanceMeters: item.distanceMeters,
			distanceKm: item.distanceKm,
			locale: i18n.language,
		});
	const badges = item.deliveryBadges ?? [];
	const showDiscount =
		item.discount !== undefined &&
		item.discount.originalPrice > 0 &&
		item.discount.percentage > 0;
	const combinedPaymentEnabled = isCombinedPaymentEnabled(item);
	const isPdpLayout = badgeLayout === "pdp";
	const hasListingLogo = combinedPaymentEnabled && !isPdpLayout;
	const hasPdpLogo = combinedPaymentEnabled && isPdpLayout;
	return (
		<button
			type="button"
			className={pharmacyPickerCardVariant({
				variant: isSelected ? "selected" : "normal",
			})}
			onClick={onSelect}
			aria-pressed={isSelected}
			data-testid="pharmacy-picker-card"
			data-pharmacy-id={item.id}
		>
			<div className="flex w-full items-center justify-between gap-2">
				<div className="flex items-center gap-1 min-w-0">
					{item.featured && <TopPharmacyBadge />}
					<span
						className={cn(
							"text-body-bold truncate text-start min-w-0",
							isSelected ? "text-ansay-dark" : "text-gray-700",
						)}
						title={item.name}
					>
						<HighlightMatch
							text={item.name}
							query={searchQuery}
							className={cn(
								"font-[900]",
								isSelected ? "text-ansay-dark" : "text-gray-700",
							)}
						/>
					</span>
				</div>
				{showPrice && product && item.price !== undefined ? (
					<Price
						variant="raw"
						className="shrink-0 whitespace-nowrap text-body-sm-emphasis text-gray-700"
						price={item.price}
						category={product.category}
						kind={product.kind}
					/>
				) : hasListingLogo ? (
					<LogoPay
						title="DrAnsay Pay"
						className="h-5 w-9 shrink-0"
						data-testid="pharmacy-picker-combined-payment-badge"
					/>
				) : null}
			</div>
			<div className="flex flex-col gap-2 w-full min-w-0">
				<div className="flex w-full items-start justify-between gap-3">
					{/* Jupiter DS "Pharmacy selection" (Figma 4597:58902): the distance
					    sits directly to the right of the address text, not pushed to
					    the row's far edge. */}
					<div className="flex min-w-0 flex-1 items-center gap-1">
						{item.address ? (
							<span
								className="min-w-0 truncate text-body-sm text-gray-500"
								title={item.address}
							>
								<HighlightMatch text={item.address} query={searchQuery} />
							</span>
						) : null}
						{distanceLabel && (
							<span className="shrink-0 whitespace-nowrap text-body-sm-emphasis text-gray-700">
								{distanceLabel}
							</span>
						)}
					</div>
					<div className="flex shrink-0 items-center justify-end gap-1">
						{showDiscount && item.discount && product ? (
							<div className="flex items-center gap-1">
								<PriceComparisonOldPrice
									productCategory={product.category}
									productKind={product.kind}
									priceComparisonDiscount={{
										visible: true,
										nonDiscountedPrice: item.discount.originalPrice,
										discountPercentage: item.discount.percentage,
									}}
								/>
								<PriceComparisonDiscountBadge
									discountPercentage={item.discount?.percentage}
								/>
							</div>
						) : null}
						{hasPdpLogo && (
							<LogoPay
								title="DrAnsay Pay"
								className={cn(
									"shrink-0 h-5 w-9 md:h-6 md:w-[43.2px]",
									showDiscount && "ml-1",
								)}
								data-testid="pharmacy-picker-combined-payment-badge"
							/>
						)}
					</div>
				</div>
				{badges.length > 0 && (
					<div className="flex flex-wrap items-center gap-1">
						{badges.map((badge) => (
							<div
								key={badge.kind}
								className={cn(
									"inline-flex items-center gap-1 rounded bg-ansay-background text-caption text-ansay-dark h-6",
									badge.label ? "px-2 py-1" : "w-6 justify-center",
								)}
							>
								{getDeliveryIcon(badge.kind)}
								{badge.label && <span>{badge.label}</span>}
							</div>
						))}
					</div>
				)}
			</div>
		</button>
	);
}
